import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdToken
} from 'firebase/auth';
import { notification } from 'antd';
import { auth } from '../config/firebase';
import api, { authApi } from '../services/api';
import { getString } from '../utils/translations';
import {
  getSelectableVideoOrganizations,
  getVideoProcessingAccess,
} from '../utils/videoProcessingAccess';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [needsOrganizationSelection, setNeedsOrganizationSelection] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectingOrganization, setSelectingOrganization] = useState(false);
  const [authToken, setAuthToken] = useState(null);

  const clearAuthState = () => {
    setUser(null);
    setOrganization(null);
    setOrganizations([]);
    setNeedsOrganizationSelection(false);
    setAuthToken(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const applyAuthContext = (context, options = {}) => {
    const { allowSelectedOrg = false } = options;
    const availableOrganizations = getSelectableVideoOrganizations(context.organizations);
    const selectedOrg = context.selectedOrg || context.organization || null;
    const singleOrganization = availableOrganizations.length === 1 ? availableOrganizations[0] : null;

    setOrganizations(availableOrganizations);

    if (availableOrganizations.length > 1 && !allowSelectedOrg) {
      setOrganization(null);
      setNeedsOrganizationSelection(true);
      return { organization: null, requiresSelection: true };
    }

    const resolvedOrganization = selectedOrg || singleOrganization;
    setOrganization(resolvedOrganization);
    setNeedsOrganizationSelection(false);
    return { organization: resolvedOrganization, requiresSelection: false };
  };

  const loadAuthContext = async (idToken, options = {}) => {
    api.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
    const context = await authApi.getContext();
    return applyAuthContext(context, options);
  };

  // Initialize auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get the ID token for SDK backend authentication
          const idToken = await getIdToken(firebaseUser);
          setAuthToken(idToken);
          
          // Validate with SDK backend and get organization context
          const { organization: orgContext, requiresSelection } = await loadAuthContext(idToken);
          
          if (orgContext || requiresSelection) {
            setUser(firebaseUser);
            if (orgContext) {
              console.log('[auth] ✅ User authenticated with organization:', orgContext.name);
            } else {
              console.log('[auth] User authenticated; organization selection required');
            }
          } else {
            console.warn('[auth] ⚠️ SDK backend validation failed');
            clearAuthState();
          }
        } catch (error) {
          console.error('[auth] Error during authentication:', error);
          notification.error({
            message: 'Authentication Error',
            description: 'Failed to authenticate with Video Dashboard Pro',
          });
          clearAuthState();
        }
      } else {
        clearAuthState();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign in function
  const signIn = async (email, password) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      notification.success({
        message: 'Signed In',
        description: 'Welcome back to Video Dashboard Pro!',
      });
      return true;
    } catch (error) {
      console.error('[auth] Sign in error:', error);
      notification.error({
        message: 'Sign In Failed',
        description: error.message || 'Failed to sign in',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOutUser = async () => {
    try {
      await signOut(auth);
      
      clearAuthState();
      
      notification.info({
        message: 'Signed Out',
        description: 'You have been signed out successfully',
      });
      return true;
    } catch (error) {
      console.error('[auth] Sign out error:', error);
      notification.error({
        message: getString('pipeline.msg.sign_out_failed', 'Sign Out Failed'),
        description: error.message || getString('pipeline.msg.failed_to_sign_out', 'Failed to sign out'),
      });
      return false;
    }
  };

  // Refresh organization data
  const refreshOrganization = async () => {
    if (!authToken) return null;
    
    try {
      const { organization: orgContext } = await loadAuthContext(authToken, { allowSelectedOrg: true });
      return orgContext;
    } catch (error) {
      console.error('[auth] Failed to refresh organization data:', error);
      return null;
    }
  };

  const selectOrganization = async (orgId) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      notification.error({
        message: getString('pipeline.auth.selection_failed', 'Organization Selection Failed'),
        description: getString('pipeline.auth.sign_in_required', 'Please sign in again before selecting an organization.'),
      });
      return false;
    }

    setSelectingOrganization(true);
    try {
      const selection = await authApi.selectOrganization(orgId);
      const refreshedToken = await getIdToken(firebaseUser, true);
      setAuthToken(refreshedToken);

      const { organization: orgContext } = await loadAuthContext(refreshedToken, { allowSelectedOrg: true });
      const selectedOrg = orgContext || selection.organization || selection.selectedOrg;

      if (!selectedOrg) {
        throw new Error('No selected organization returned');
      }

      setOrganization(selectedOrg);
      setNeedsOrganizationSelection(false);

      notification.success({
        message: getString('pipeline.auth.organization_selected', 'Organization Selected'),
        description: selectedOrg.name,
      });

      return true;
    } catch (error) {
      console.error('[auth] Failed to select organization:', error.response?.data || error.message);
      notification.error({
        message: getString('pipeline.auth.selection_failed', 'Organization Selection Failed'),
        description: error.response?.data?.message || error.message || getString('pipeline.auth.selection_failed_desc', 'Failed to switch organization'),
      });
      return false;
    } finally {
      setSelectingOrganization(false);
    }
  };

  const {
    hasVideoProcessingWriteAccess,
    isReadOnlyMode,
  } = getVideoProcessingAccess(organization);

  const value = {
    user,
    organization,
    organizations,
    authToken,
    loading,
    selectingOrganization,
    needsOrganizationSelection,
    signIn,
    signOut: signOutUser,
    refreshOrganization,
    selectOrganization,
    isAuthenticated: !!user && (!!organization || needsOrganizationSelection),
    hasVideoProcessingAccess: organization?.videoProcessingEnabled || false,
    hasVideoProcessingWriteAccess,
    isReadOnlyMode,
    organizationUsage: {
      used: organization?.videoProcessingUsage || 0,
      limit: organization?.videoProcessingLimit || 0,
      remaining: organization ? (organization.videoProcessingLimit - organization.videoProcessingUsage) : 0,
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
